
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, MapPin, DollarSign } from 'lucide-react';
import BackButton from '../common/BackButton';
import { madeInsAPI, wiperProductsAPI, locationsAPI, zonesAPI } from '../../services/garageApi';

const CreateWiperProduct = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);

    const [formData, setFormData] = useState({
        size: '',
        type: '',
        frame_material: '',
        compatibility_year: new Date().getFullYear(),
        compatible_cars: '',
        made_in_id: '',
        // Pricing fields
        cost_price: '',
        selling_price: '',
        tax_rate: '',
        is_tax_inclusive: false,
        notes: '',
        status: 'active'
    });

    const [madeIns, setMadeIns] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Distribution state
    const [totalQuantity, setTotalQuantity] = useState('');
    const [distributions, setDistributions] = useState([{ location_id: '', zone_id: '', quantity: '' }]);
    const [zonesCache, setZonesCache] = useState({});
    const [inventoryChanged, setInventoryChanged] = useState(false);

    // Helper function to extract data from API responses
    const extractData = (response) => {
        if (!response) return [];

        if (Array.isArray(response.data)) {
            return response.data;
        }

        if (response.data && typeof response.data === 'object') {
            if (response.data.data) {
                if (Array.isArray(response.data.data)) {
                    return response.data.data;
                }
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    return response.data.data.data;
                }
            }
            if (response.data.success !== undefined && response.data.data) {
                if (Array.isArray(response.data.data)) {
                    return response.data.data;
                }
            }
        }

        return [];
    };

    const fetchMadeIns = useCallback(async () => {
        try {
            const response = await madeInsAPI.getAll();
            const madeInsData = extractData(response);
            setMadeIns(madeInsData);
        } catch (error) {
            console.error('Error fetching made ins:', error);
            setMadeIns([]);
        }
    }, []);

    const fetchLocations = useCallback(async () => {
        try {
            const response = await locationsAPI.getAll({ per_page: 100 });
            const locationsData = extractData(response);
            setLocations(locationsData);
        } catch (error) {
            console.error('Error fetching locations:', error);
            setLocations([]);
        }
    }, []);

    const fetchZonesForLocation = async (locationId) => {
        if (!locationId || zonesCache[locationId]) return;
        try {
            const response = await zonesAPI.getByLocation(locationId);
            const zonesData = response?.data?.data?.data || response?.data?.data || response?.data || [];
            setZonesCache(prev => ({ ...prev, [locationId]: Array.isArray(zonesData) ? zonesData : [] }));
        } catch (error) {
            setZonesCache(prev => ({ ...prev, [locationId]: [] }));
        }
    };

    const fetchWiperProduct = useCallback(async () => {
        try {
            setLoading(true);
            const response = await wiperProductsAPI.show(id);

            const wiper = response.data || {};

            if (!wiper || !wiper.id) {
                throw new Error('Wiper product not found');
            }

            setFormData({
                size: wiper.size || '',
                type: wiper.type || '',
                frame_material: wiper.frame_material || '',
                compatibility_year: wiper.compatibility_year || new Date().getFullYear(),
                compatible_cars: wiper.compatible_cars || '',
                made_in_id: wiper.made_in_id || wiper.made_in?.id || '',
                cost_price: wiper.cost_price || '',
                selling_price: wiper.selling_price || '',
                tax_rate: wiper.tax_rate?.toString() || '',
                is_tax_inclusive: !!wiper.is_tax_inclusive,
                notes: wiper.notes || '',
                status: wiper.active === false ? 'inactive' : (wiper.status || 'active')
            });

            // Populate distributions from inventories
            if (wiper.inventories && wiper.inventories.length > 0) {
                const existingDist = wiper.inventories.map(inv => ({
                    location_id: inv.location_id || '',
                    zone_id: inv.zone_id || '',
                    quantity: inv.quantity || 0,
                }));
                setDistributions(existingDist);
                setTotalQuantity(wiper.inventories.reduce((sum, inv) => sum + (inv.quantity || 0), 0).toString());

                // Pre-fetch zones for existing locations
                const locationIds = [...new Set(wiper.inventories.map(inv => inv.location_id).filter(Boolean))];
                for (const locId of locationIds) {
                    fetchZonesForLocation(locId);
                }
            } else {
                // Fallback: use quantity if no inventories
                if (wiper.quantity) {
                    setTotalQuantity(wiper.quantity.toString());
                }
            }
        } catch (error) {
            console.error('Error fetching wiper product:', error);
            alert('Error fetching wiper product details');
            navigate('/wiper-products');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, navigate]);

    useEffect(() => {
        fetchMadeIns();
        fetchLocations();
        if (isEditing) {
            fetchWiperProduct();
        }
    }, [fetchMadeIns, fetchLocations, fetchWiperProduct, isEditing]);

    // Distribution row management
    const handleDistributionChange = (index, field, value) => {
        const updated = [...distributions];
        updated[index] = { ...updated[index], [field]: value };

        if (field === 'location_id') {
            updated[index].zone_id = '';
            if (value) fetchZonesForLocation(value);
        }

        setDistributions(updated);

        if (isEditing) {
            setInventoryChanged(true);
        }

        if (errors.distributions) {
            setErrors(prev => ({ ...prev, distributions: '' }));
        }
    };

    const addDistributionRow = () => {
        setDistributions(prev => [...prev, { location_id: '', zone_id: '', quantity: '' }]);
        if (isEditing) {
            setInventoryChanged(true);
        }
    };

    const removeDistributionRow = (index) => {
        if (distributions.length <= 1) return;
        setDistributions(prev => prev.filter((_, i) => i !== index));
        if (isEditing) {
            setInventoryChanged(true);
        }
    };

    const getAllocatedQuantity = () => {
        return distributions.reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
    };

    const getRemainingQuantity = () => {
        const total = parseInt(totalQuantity) || 0;
        return total - getAllocatedQuantity();
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.size.trim()) newErrors.size = 'Size is required';
        if (!formData.type.trim()) newErrors.type = 'Type is required';
        if (!formData.frame_material.trim()) newErrors.frame_material = 'Frame material is required';
        if (!formData.compatibility_year) newErrors.compatibility_year = 'Compatibility year is required';
        if (!formData.compatible_cars.trim()) newErrors.compatible_cars = 'Compatible cars is required';
        if (!formData.made_in_id) newErrors.made_in_id = 'Made in country is required';

        if (!formData.cost_price) newErrors.cost_price = 'Cost price is required';
        if (!formData.selling_price) newErrors.selling_price = 'Selling price is required';
        if (formData.cost_price && formData.cost_price < 0) newErrors.cost_price = 'Cost price cannot be negative';
        if (formData.selling_price && formData.selling_price < 0) newErrors.selling_price = 'Selling price cannot be negative';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            const submitData = {
                size: formData.size,
                type: formData.type,
                frame_material: formData.frame_material,
                compatibility_year: formData.compatibility_year,
                compatible_cars: formData.compatible_cars,
                made_in_id: formData.made_in_id,
                cost_price: parseFloat(formData.cost_price || 0),
                selling_price: parseFloat(formData.selling_price || 0),
                tax_rate: parseFloat(formData.tax_rate) || 0,
                is_tax_inclusive: formData.is_tax_inclusive ? 1 : 0,
                notes: formData.notes || '',
                active: formData.status === 'active' ? 1 : 0,
                total_quantity: parseInt(totalQuantity) || 0,
            };

            if (isEditing && inventoryChanged) {
                submitData._inventory_changed = '1';
            }

            const filledDist = distributions.filter(d => d.location_id && d.quantity);
            if (filledDist.length > 0) {
                submitData.distributions = JSON.stringify(filledDist.map(d => ({
                    location_id: d.location_id,
                    zone_id: d.zone_id || null,
                    quantity: parseInt(d.quantity),
                })));
            }

            if (isEditing) {
                await wiperProductsAPI.update(id, submitData);
            } else {
                await wiperProductsAPI.create(submitData);
            }

            navigate('/wiper-products', {
                state: { message: `Wiper product ${isEditing ? 'updated' : 'created'} successfully!` }
            });
        } catch (error) {
            console.error('Error saving wiper:', error);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else {
                alert(error.response?.data?.message || 'Error saving wiper product. Please check all required fields.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const calculateProfit = () => {
        const cost = parseFloat(formData.cost_price) || 0;
        const selling = parseFloat(formData.selling_price) || 0;
        const taxRate = parseFloat(formData.tax_rate) || 0;
        const profit = selling - cost;
        const margin = cost > 0 ? (profit / cost * 100).toFixed(2) : 0;
        const finalPrice = formData.is_tax_inclusive
            ? selling
            : selling * (1 + taxRate / 100);
        return { profit, margin, finalPrice };
    };

    if (loading && isEditing) {
        return <div className="flex justify-center items-center h-64">Loading...</div>;
    }

    const remaining = getRemainingQuantity();

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
                <BackButton to="/wiper-products" />
                <h5 className="text-lg font-semibold">
                    {isEditing ? 'Edit Wiper Product' : 'Create New Wiper Product'}
                </h5>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Product Information Section */}
                <div className="border-b pb-6">
                    <h6 className="text-md font-medium text-gray-800 mb-4">Product Information</h6>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Size (inches) *
                            </label>
                            <input
                                type="text"
                                value={formData.size}
                                onChange={(e) => handleInputChange('size', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md ${errors.size ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="e.g., 22"
                                required
                            />
                            {errors.size && <p className="text-red-500 text-sm mt-1">{errors.size}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type *
                            </label>
                            <input
                                type="text"
                                value={formData.type}
                                onChange={(e) => handleInputChange('type', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md ${errors.type ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="e.g., Beam, Hybrid"
                                required
                            />
                            {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Frame Material *
                            </label>
                            <input
                                type="text"
                                value={formData.frame_material}
                                onChange={(e) => handleInputChange('frame_material', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md ${errors.frame_material ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="e.g., Steel, Rubber"
                                required
                            />
                            {errors.frame_material && <p className="text-red-500 text-sm mt-1">{errors.frame_material}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Compatibility Year *
                            </label>
                            <input
                                type="number"
                                value={formData.compatibility_year}
                                onChange={(e) => handleInputChange('compatibility_year', parseInt(e.target.value))}
                                className={`w-full px-3 py-2 border rounded-md ${errors.compatibility_year ? 'border-red-500' : 'border-gray-300'}`}
                                min="1900"
                                max={new Date().getFullYear() + 1}
                                required
                            />
                            {errors.compatibility_year && <p className="text-red-500 text-sm mt-1">{errors.compatibility_year}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Made In *
                            </label>
                            <select
                                value={formData.made_in_id}
                                onChange={(e) => handleInputChange('made_in_id', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md ${errors.made_in_id ? 'border-red-500' : 'border-gray-300'}`}
                                required
                            >
                                <option value="">Select Country</option>
                                {madeIns.map(madeIn => (
                                    <option key={madeIn.id} value={madeIn.id}>{madeIn.name}</option>
                                ))}
                            </select>
                            {errors.made_in_id && <p className="text-red-500 text-sm mt-1">{errors.made_in_id}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="discontinued">Discontinued</option>
                            </select>
                        </div>
                    </div>

                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Compatible Cars *
                        </label>
                        <textarea
                            value={formData.compatible_cars}
                            onChange={(e) => handleInputChange('compatible_cars', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md ${errors.compatible_cars ? 'border-red-500' : 'border-gray-300'}`}
                            rows="3"
                            placeholder="e.g., Toyota Camry, Honda Accord"
                            required
                        />
                        {errors.compatible_cars && <p className="text-red-500 text-sm mt-1">{errors.compatible_cars}</p>}
                    </div>

                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notes
                        </label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => handleInputChange('notes', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows="2"
                            placeholder="Any additional notes..."
                        />
                    </div>
                </div>

                {/* Pricing Section */}
                <div className="border-b pb-6">
                    <h6 className="text-md font-medium text-gray-800 mb-4">Pricing</h6>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Cost Price <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.cost_price}
                                    onChange={(e) => handleInputChange('cost_price', e.target.value)}
                                    placeholder="0.00"
                                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.cost_price ? 'border-red-300' : 'border-gray-200'
                                    }`}
                                />
                            </div>
                            {errors.cost_price && <p className="mt-1 text-sm text-red-600">{errors.cost_price}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Selling Price <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <DollarSign className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.selling_price}
                                    onChange={(e) => handleInputChange('selling_price', e.target.value)}
                                    placeholder="0.00"
                                    className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.selling_price ? 'border-red-300' : 'border-gray-200'
                                    }`}
                                />
                            </div>
                            {errors.selling_price && <p className="mt-1 text-sm text-red-600">{errors.selling_price}</p>}
                        </div>
                    </div>

                    {/* Tax Configuration */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tax Rate (%)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={formData.tax_rate}
                                onChange={(e) => handleInputChange('tax_rate', e.target.value)}
                                placeholder="0.00"
                                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    errors.tax_rate ? 'border-red-300' : 'border-gray-200'
                                }`}
                            />
                            {errors.tax_rate && <p className="mt-1 text-sm text-red-600">{errors.tax_rate}</p>}
                        </div>
                        <div className="flex items-center">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.is_tax_inclusive}
                                    onChange={(e) => handleInputChange('is_tax_inclusive', e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                <span className="ml-3 text-sm font-medium text-gray-700">Tax Inclusive</span>
                            </label>
                            <p className="ml-2 text-xs text-gray-500">
                                {formData.is_tax_inclusive
                                    ? 'Selling price already includes tax'
                                    : 'Tax will be added to selling price'}
                            </p>
                        </div>
                    </div>

                    {formData.cost_price && formData.selling_price && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-blue-900">Profit Margin</p>
                                    <p className="text-2xl font-bold text-blue-900">
                                        ${calculateProfit().profit.toFixed(2)}
                                    </p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm text-blue-900">Margin %</p>
                                    <p className="text-2xl font-bold text-blue-900">
                                        {calculateProfit().margin}%
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-blue-900">Final Price (incl. tax)</p>
                                    <p className="text-xs text-blue-700 mt-0.5">
                                        {formData.is_tax_inclusive
                                            ? 'Tax included in selling price'
                                            : `Selling Price + ${formData.tax_rate || 0}% tax`}
                                    </p>
                                    <p className="text-2xl font-bold text-green-700">
                                        ${calculateProfit().finalPrice.toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Inventory Distribution Section */}
                <div className="border-b pb-6">
                    <h6 className="text-md font-medium text-gray-800 mb-4">Inventory Distribution</h6>

                    {/* Total Quantity */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Quantity
                        </label>
                        <input
                            type="number"
                            value={totalQuantity}
                            onChange={(e) => {
                                setTotalQuantity(e.target.value);
                                if (isEditing) setInventoryChanged(true);
                            }}
                            className={`w-full max-w-xs px-3 py-2 border rounded-md ${errors.total_quantity ? 'border-red-500' : 'border-gray-300'}`}
                            placeholder="Enter total quantity"
                            min="0"
                        />
                        {errors.total_quantity && <p className="text-red-500 text-sm mt-1">{errors.total_quantity}</p>}
                    </div>

                    {/* Distribution Rows */}
                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                            Distribution (optional - allocate stock to locations/zones)
                        </label>

                        {distributions.map((dist, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                <MapPin className="w-4 h-4 text-gray-400 mt-2.5 flex-shrink-0" />

                                {/* Location */}
                                <div className="flex-1">
                                    <select
                                        value={dist.location_id}
                                        onChange={(e) => handleDistributionChange(index, 'location_id', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    >
                                        <option value="">Select Location</option>
                                        {locations.map(loc => (
                                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Zone */}
                                <div className="flex-1">
                                    <select
                                        value={dist.zone_id}
                                        onChange={(e) => handleDistributionChange(index, 'zone_id', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        disabled={!dist.location_id}
                                    >
                                        <option value="">
                                            {!dist.location_id ? 'Select location first' :
                                             (zonesCache[dist.location_id] || []).length === 0 ? 'No zones available' : 'Select Zone'}
                                        </option>
                                        {(zonesCache[dist.location_id] || []).map(zone => (
                                            <option key={zone.id} value={zone.id}>{zone.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Quantity */}
                                <div className="w-28">
                                    <input
                                        type="number"
                                        value={dist.quantity}
                                        onChange={(e) => handleDistributionChange(index, 'quantity', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        placeholder="Qty"
                                        min="0"
                                    />
                                </div>

                                {/* Remove */}
                                <button
                                    type="button"
                                    onClick={() => removeDistributionRow(index)}
                                    disabled={distributions.length <= 1}
                                    className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                    title="Remove row"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={addDistributionRow}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Add Distribution Row
                        </button>

                        {/* Allocation Summary */}
                        {parseInt(totalQuantity) > 0 && (
                            <div className={`text-sm mt-2 p-2 rounded ${remaining === 0 ? 'bg-green-50 text-green-700' : remaining > 0 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
                                Allocated: {getAllocatedQuantity()} / {totalQuantity}
                                {remaining > 0 && ` (${remaining} unallocated - will go to default location)`}
                                {remaining < 0 && ` (over-allocated by ${Math.abs(remaining)})`}
                                {remaining === 0 && ' - Fully allocated'}
                            </div>
                        )}

                        <p className="text-xs text-gray-500 mt-1">
                            Note: Any quantity not distributed to specific locations will be placed in the default unallocated zone.
                        </p>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/wiper-products')}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Saving...' : (isEditing ? 'Update Product' : 'Create Product')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateWiperProduct;
